/* PulseScrubs — Product Form & Image Gallery */

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.variantData = JSON.parse(this.querySelector('[data-variant-json]')?.textContent || '[]');
    this.selectedOptions = {};

    this.bindEvents();
    this.initSelectedVariant();
  }

  bindEvents() {
    this.querySelectorAll('[data-option]').forEach(el => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.selectOption(el);
      });
    });

    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.addToCart();
    });
  }

  initSelectedVariant() {
    this.querySelectorAll('[data-option].is-active').forEach(el => {
      this.selectedOptions[el.dataset.optionName] = el.dataset.option;
    });
    this.updateVariant();
  }

  selectOption(el) {
    const name = el.dataset.optionName;
    const value = el.dataset.option;

    el.closest('.variant-selector__options')
      .querySelectorAll('[data-option]')
      .forEach(opt => opt.classList.remove('is-active'));
    el.classList.add('is-active');

    this.selectedOptions[name] = value;
    this.updateVariant();
  }

  updateVariant() {
    const selectedValues = Object.values(this.selectedOptions);
    const variant = this.variantData.find(v => {
      return v.options.every((opt, i) => opt === selectedValues[i]);
    });

    if (!variant) return;

    const variantInput = this.querySelector('[name="id"]');
    if (variantInput) variantInput.value = variant.id;

    const priceEl = this.querySelector('[data-product-price]');
    if (priceEl) {
      priceEl.innerHTML = this.formatPrice(variant.price, variant.compare_at_price);
    }

    const addBtn = this.querySelector('[data-add-to-cart]');
    if (addBtn) {
      if (variant.available) {
        addBtn.disabled = false;
        addBtn.textContent = addBtn.dataset.textAdd || 'Add to Cart';
      } else {
        addBtn.disabled = true;
        addBtn.textContent = addBtn.dataset.textSoldout || 'Sold Out';
      }
    }

    this.updateSoldOutStates();

    if (variant.featured_image) {
      const gallery = document.querySelector('product-gallery');
      if (gallery) gallery.setImage(variant.featured_image.src);
    }

    this.dispatchEvent(new CustomEvent('variant:changed', { detail: variant, bubbles: true }));
  }

  updateSoldOutStates() {
    this.querySelectorAll('.size-btn[data-option]').forEach(btn => {
      const testOptions = { ...this.selectedOptions, [btn.dataset.optionName]: btn.dataset.option };
      const testValues = Object.values(testOptions);
      const matchingVariant = this.variantData.find(v =>
        v.options.every((opt, i) => opt === testValues[i])
      );
      btn.classList.toggle('is-sold-out', matchingVariant && !matchingVariant.available);
    });
  }

  async addToCart() {
    const variantId = this.querySelector('[name="id"]')?.value;
    const quantity = parseInt(this.querySelector('.quantity-selector__input')?.value || 1);

    if (!variantId) return;

    const addBtn = this.querySelector('[data-add-to-cart]');
    if (addBtn) {
      addBtn.disabled = true;
      addBtn.textContent = 'Adding...';
    }

    const success = await addToCart(parseInt(variantId), quantity);

    if (addBtn) {
      addBtn.disabled = false;
      addBtn.textContent = success ? 'Added!' : 'Add to Cart';
      if (success) {
        setTimeout(() => {
          addBtn.textContent = addBtn.dataset.textAdd || 'Add to Cart';
        }, 2000);
      }
    }
  }

  formatPrice(price, compareAt) {
    const fmt = (cents) => (cents / 100).toLocaleString('en-PK', {
      style: 'currency', currency: 'PKR', minimumFractionDigits: 0
    });

    if (compareAt && compareAt > price) {
      return `<span class="price__sale">${fmt(price)}</span> <span class="price__compare">${fmt(compareAt)}</span>`;
    }
    return `<span class="price__regular">${fmt(price)}</span>`;
  }
}
customElements.define('product-form', ProductForm);

/* --- Product Image Gallery --- */
class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.mainImage = this.querySelector('.product-gallery__main img');
    this.thumbs = this.querySelectorAll('.product-gallery__thumb');

    this.thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        this.setImageByThumb(thumb);
      });
    });

    this.initTouchSwipe();
  }

  setImageByThumb(thumb) {
    const src = thumb.dataset.src;
    const srcset = thumb.dataset.srcset || '';
    if (!src || !this.mainImage) return;

    this.mainImage.src = src;
    if (srcset) this.mainImage.srcset = srcset;

    this.thumbs.forEach(t => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
  }

  setImage(src) {
    if (!this.mainImage) return;
    this.mainImage.src = src;

    const matchingThumb = Array.from(this.thumbs).find(t => t.dataset.src === src);
    if (matchingThumb) {
      this.thumbs.forEach(t => t.classList.remove('is-active'));
      matchingThumb.classList.add('is-active');
    }
  }

  initTouchSwipe() {
    const main = this.querySelector('.product-gallery__main');
    if (!main) return;

    let startX = 0;
    const images = Array.from(this.thumbs);

    main.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    }, { passive: true });

    main.addEventListener('touchend', (e) => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) < 50) return;

      const currentIndex = images.findIndex(t => t.classList.contains('is-active'));
      const nextIndex = diff > 0
        ? Math.min(currentIndex + 1, images.length - 1)
        : Math.max(currentIndex - 1, 0);

      if (images[nextIndex]) this.setImageByThumb(images[nextIndex]);
    }, { passive: true });
  }
}
customElements.define('product-gallery', ProductGallery);
